import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listenDriverLocations, phoneHref, waHref, formatPhone } from '../services/firebase';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const CENTER = {
  lat: Number(process.env.REACT_APP_MAP_CENTER_LAT) || 13.6549,
  lng: Number(process.env.REACT_APP_MAP_CENTER_LNG) || 78.9471,
};
const MAX_AGE_MS = 3 * 60 * 1000;

const formatAge = (ms) => {
  if (!Number.isFinite(ms)) return 'no location yet';
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  return min === 1 ? '1 min ago' : `${min} min ago`;
};

const PILER_CLUSTERS = [
  { name: 'APSRTC Bus Stand 🚌', center: { lat: 13.6547389, lng: 78.9445406 }, radius: 300, color: '#00A86B' },
  { name: 'Railway Station 🚆', center: { lat: 13.6625162, lng: 78.9280908 }, radius: 250, color: '#0087FF' },
  { name: 'Govt Hospital 🏥', center: { lat: 13.654472, lng: 78.9418376 }, radius: 250, color: '#FF4D4D' },
  { name: 'Main Market 🛍️', center: { lat: 13.6549, lng: 78.9471 }, radius: 350, color: '#FFA500' },
];

function loadMaps() {
  if (window.google?.maps) return Promise.resolve();
  if (!MAPS_KEY) return Promise.reject(new Error('missing_maps_key'));
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-mt-maps]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}`;
    s.async = true;
    s.dataset.mtMaps = '1';
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function LiveMap({ drivers = [], rides = [] }) {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markers = useRef({});
  const polylines = useRef([]);
  const routeMarkers = useRef([]);
  const [locs, setLocs] = useState([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [filterState, setFilterState] = useState('all'); // 'all' | 'free' | 'trip' | 'arriving' | 'reconnecting'
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  // GPS freshness must continue to change even when RTDB is quiet. Without a
  // clock tick, a pin whose last update is three minutes old could remain
  // green indefinitely until some unrelated Firestore/RTDB event re-rendered
  // this component.
  const [freshnessNow, setFreshnessNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setFreshnessNow(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, []);

  const activeRidesByDriver = useMemo(() => {
    const map = {};
    rides.forEach((r) => {
      if (r.driverId && ['driver_assigned', 'arrived', 'started'].includes(r.status)) {
        map[r.driverId] = r;
      }
    });
    return map;
  }, [rides]);

  const driverStatsMap = useMemo(() => {
    const map = {};
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    drivers.forEach((d) => {
      const dRides = rides.filter((r) => r.driverId === d.id);
      const todayRides = dRides.filter((r) => {
        const t = r.completedAt?.toDate ? r.completedAt.toDate() : (r.completedAt ? new Date(r.completedAt) : null);
        return t && t >= now && r.status === 'completed';
      });
      const todayGross = todayRides.reduce((s, r) => s + (Number(r.fare) || 0), 0);
      const ratedRides = dRides.filter((r) => r.rating);
      const avgRating = ratedRides.length
        ? (ratedRides.reduce((s, r) => s + Number(r.rating), 0) / ratedRides.length).toFixed(1)
        : null;

      map[d.id] = {
        todayRides: todayRides.length,
        todayGross,
        avgRating,
      };
    });
    return map;
  }, [drivers, rides]);

  const driverMap = useMemo(() => {
    const m = {};
    drivers.forEach((d) => { m[d.id] = d; });
    return m;
  }, [drivers]);

  // Every driver actually online right now — the source of truth for who
  // should be accounted for on this map. Locations (locsById below) are
  // looked up per driver, not the other way around: a driver online with no
  // location entry (or a stale one) must still be counted, not silently
  // dropped just because `locs` has nothing for her.
  const onlineApprovedDrivers = useMemo(
    () => drivers.filter((d) => d.isOnline && d.isApproved && !d.isBlocked),
    [drivers],
  );

  const locsById = useMemo(() => {
    const m = {};
    locs.forEach((l) => { m[l.id] = l; });
    return m;
  }, [locs]);

  // Derived live selected driver object (100% reactive to GPS & status updates)
  const selectedDriver = useMemo(() => {
    if (!selectedDriverId) return null;
    const driver = driverMap[selectedDriverId];
    if (!driver) return null;
    const loc = locsById[selectedDriverId];
    const activeTrip = activeRidesByDriver[selectedDriverId];
    const age = loc?.updatedAt ? freshnessNow - Number(loc.updatedAt) : Infinity;
    // She's in this map at all only because driver.isOnline is true (see
    // onlineApprovedDrivers) — a stale/missing fix means reconnecting, never
    // "offline", since she hasn't tapped Go Offline.
    const isReconnecting = age > MAX_AGE_MS;

    let state = 'online_free';
    let stateLabel = 'Free & Available';
    if (isReconnecting) {
      state = 'reconnecting';
      stateLabel = loc ? `GPS reconnecting · last updated ${formatAge(age)}` : 'Online · no location yet';
    } else if (activeTrip?.status === 'started') {
      state = 'trip';
      stateLabel = 'On Active Trip';
    } else if (activeTrip?.status === 'driver_assigned' || activeTrip?.status === 'arrived') {
      state = 'arriving';
      stateLabel = 'Arriving at Pickup';
    }

    return {
      ...driver,
      location: loc,
      state,
      stateLabel,
      activeTrip,
      stats: driverStatsMap[selectedDriverId] || { todayRides: 0, todayGross: 0, avgRating: null },
    };
  }, [selectedDriverId, driverMap, locsById, activeRidesByDriver, driverStatsMap, freshnessNow]);

  useEffect(() => listenDriverLocations(setLocs), []);

  useEffect(() => {
    let cancelled = false;
    loadMaps()
      .then(() => {
        if (cancelled || !mapRef.current) return;
        mapInst.current = new window.google.maps.Map(mapRef.current, {
          center: CENTER,
          zoom: 14,
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          styles: [
            { elementType: 'geometry', stylers: [{ color: '#131b26' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#9baec8' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#131b26' }] },
            { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#27384c' }] },
            { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3b5370' }] },
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#090e14' }] },
          ],
        });
        setReady(true);
        PILER_CLUSTERS.forEach((c) => {
          new window.google.maps.Marker({
            position: c.center,
            map: mapInst.current,
            title: c.name,
            label: { text: c.name, fontSize: '11px', color: '#ffffff', fontWeight: 'bold' },
            icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 0 },
          });
        });
      })
      .catch(() => setError('Maps failed to load. Set REACT_APP_GOOGLE_MAPS_KEY and allow this domain.'));
    return () => { cancelled = true; };
  }, []);

  const emojiFor = (state) => (
    state === 'trip' ? '🔵' : state === 'arriving' ? '🟡' : state === 'reconnecting' ? '🟠' : '🟢'
  );

  // Update Driver Markers with Multi-State Colors
  useEffect(() => {
    if (!ready || !mapInst.current) return;
    const seen = new Set();

    // Every online driver is accounted for here, not just the ones `locs`
    // happens to have an entry for — a driver online with no location data
    // (or stale data) used to be entirely invisible, not even a faded dot.
    onlineApprovedDrivers.forEach((driver) => {
      const loc = locsById[driver.id];
      const age = loc?.updatedAt ? freshnessNow - Number(loc.updatedAt) : Infinity;
      // She's online by construction (the filter above) — stale/missing data
      // means reconnecting, never "offline": she hasn't tapped Go Offline.
      const isReconnecting = age > MAX_AGE_MS;
      const activeTrip = activeRidesByDriver[driver.id];

      let state = 'online_free'; // 🟢
      let stateLabel = 'Free & Available';

      if (isReconnecting) {
        state = 'reconnecting';
        stateLabel = loc ? `GPS reconnecting · last updated ${formatAge(age)}` : 'Online · no location yet';
      } else if (activeTrip?.status === 'started') {
        state = 'trip';
        stateLabel = 'On Active Trip';
      } else if (activeTrip?.status === 'driver_assigned' || activeTrip?.status === 'arrived') {
        state = 'arriving';
        stateLabel = 'Arriving at Pickup';
      }

      // Apply Filter
      if (filterState === 'free' && state !== 'online_free') return;
      if (filterState === 'trip' && state !== 'trip') return;
      if (filterState === 'arriving' && state !== 'arriving') return;
      if (filterState === 'reconnecting' && state !== 'reconnecting') return;

      // Nothing to place a pin at yet — she's still counted in the stats
      // bar below, just has no coordinate to draw. Drop any marker left
      // over from before she lost her location entirely.
      if (!loc?.lat || !loc?.lng) return;

      seen.add(driver.id);
      const pos = { lat: Number(loc.lat), lng: Number(loc.lng) };
      const title = `${driver.name || 'Anna'} (${driver.vehicleNumber || ''})${state === 'reconnecting' ? ` — ${stateLabel}` : ''}`;

      if (!markers.current[driver.id]) {
        const m = new window.google.maps.Marker({
          map: mapInst.current,
          position: pos,
          title,
          label: { text: emojiFor(state), fontSize: '16px' },
        });

        m.addListener('click', () => {
          setSelectedDriverId(driver.id);
        });

        markers.current[driver.id] = m;
      } else {
        markers.current[driver.id].setPosition(pos);
        markers.current[driver.id].setTitle(title);
        markers.current[driver.id].setLabel({ text: emojiFor(state), fontSize: '16px' });
      }
    });

    Object.keys(markers.current).forEach((id) => {
      if (!seen.has(id)) {
        markers.current[id].setMap(null);
        delete markers.current[id];
      }
    });
  }, [onlineApprovedDrivers, locsById, activeRidesByDriver, ready, filterState, freshnessNow]);

  // Draw Active Passenger Trip Lines
  useEffect(() => {
    if (!ready || !mapInst.current || !window.google?.maps) return;

    polylines.current.forEach((l) => l.setMap(null));
    polylines.current = [];
    routeMarkers.current.forEach((m) => m.setMap(null));
    routeMarkers.current = [];

    const activeRides = rides.filter((r) => ['searching', 'driver_assigned', 'arrived', 'started'].includes(r.status));
    activeRides.forEach((r) => {
      if (!r.pickup?.lat || !r.destination?.lat) return;
      const pickupPos = { lat: Number(r.pickup.lat), lng: Number(r.pickup.lng) };
      const dropPos = { lat: Number(r.destination.lat), lng: Number(r.destination.lng) };
      const path = [pickupPos, dropPos];
      const isSearching = r.status === 'searching';
      const polyline = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: isSearching ? '#94a3b8' : (r.status === 'started' ? '#38bdf8' : '#fbbf24'),
        strokeOpacity: isSearching ? 0.5 : 0.7,
        strokeWeight: isSearching ? 2 : 3,
        icons: isSearching ? [{
          icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
          offset: '0',
          repeat: '12px',
        }] : [],
        map: mapInst.current,
      });
      polylines.current.push(polyline);

      // A = pickup, B = drop — same green/red convention as Google's own
      // directions markers, so it reads instantly regardless of the line's
      // own status color (grey/amber/blue).
      const abMarker = (position, letter, fillColor, title) => new window.google.maps.Marker({
        position,
        map: mapInst.current,
        title,
        zIndex: 500,
        label: { text: letter, color: '#ffffff', fontSize: '11px', fontWeight: '700' },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
      routeMarkers.current.push(
        abMarker(pickupPos, 'A', '#00A86B', 'Pickup'),
        abMarker(dropPos, 'B', '#ef4444', 'Drop'),
      );
    });
  }, [ready, rides]);

  const stats = useMemo(() => {
    let free = 0;
    let onTrip = 0;
    let arriving = 0;
    let reconnecting = 0;
    let noLocation = 0;

    // Every online driver counts toward exactly one bucket here — this total
    // is what "N online" should actually mean, independent of whether the
    // map could draw a pin for her.
    onlineApprovedDrivers.forEach((d) => {
      const loc = locsById[d.id];
      const age = loc?.updatedAt ? freshnessNow - Number(loc.updatedAt) : Infinity;
      const isReconnecting = age > MAX_AGE_MS;
      const active = activeRidesByDriver[d.id];

      if (!loc) noLocation += 1;
      if (isReconnecting) reconnecting += 1;
      else if (active?.status === 'started') onTrip += 1;
      else if (active?.status === 'driver_assigned' || active?.status === 'arrived') arriving += 1;
      else free += 1;
    });

    return {
      total: onlineApprovedDrivers.length, free, onTrip, arriving, reconnecting, noLocation,
    };
  }, [onlineApprovedDrivers, locsById, activeRidesByDriver, freshnessNow]);

  // An online Anna without coordinates cannot truthfully be drawn on a map.
  // Keep her visible and actionable here instead of inventing a pin location.
  const onlineWithoutLocation = useMemo(
    () => onlineApprovedDrivers.filter((driver) => {
      const loc = locsById[driver.id];
      return !loc || !Number.isFinite(Number(loc.lat)) || !Number.isFinite(Number(loc.lng));
    }),
    [onlineApprovedDrivers, locsById],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: 'calc(100vh - 120px)' }}>
      {/* State Filter Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: '#1e293b', padding: '10px 16px', borderRadius: 12, border: '1px solid #334155' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setFilterState('all')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterState === 'all' ? '#0284c7' : '#334155', color: '#fff', border: 'none',
            }}
          >
            All Fleets ({stats.total})
          </button>
          <button
            type="button"
            onClick={() => setFilterState('free')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterState === 'free' ? '#10b981' : '#334155', color: '#fff', border: 'none',
            }}
          >
            🟢 Free Online ({stats.free})
          </button>
          <button
            type="button"
            onClick={() => setFilterState('trip')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterState === 'trip' ? '#38bdf8' : '#334155', color: '#fff', border: 'none',
            }}
          >
            🔵 On Active Trip ({stats.onTrip})
          </button>
          <button
            type="button"
            onClick={() => setFilterState('arriving')}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterState === 'arriving' ? '#fbbf24' : '#334155', color: '#fff', border: 'none',
            }}
          >
            🟡 Arriving ({stats.arriving})
          </button>
          <button
            type="button"
            onClick={() => setFilterState('reconnecting')}
            title="Still online, just quiet — she hasn't tapped Go Offline"
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: filterState === 'reconnecting' ? '#c2410c' : '#334155', color: '#fff', border: 'none',
            }}
          >
            🟠 Reconnecting ({stats.reconnecting})
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            ⚡ <strong>{stats.free + stats.onTrip + stats.arriving}</strong> Annas Live on Piler Roads
          </span>
          {stats.noLocation > 0 && (
            <span style={{ fontSize: 11, color: '#f59e0b' }}>
              ⚠ {stats.noLocation} online with no location data yet
            </span>
          )}
        </div>
      </div>

      {onlineWithoutLocation.length > 0 && (
        <section style={{ background: '#2a1b0b', border: '1px solid #92400e', borderRadius: 12, padding: '12px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div>
              <strong style={{ color: '#fbbf24', fontSize: 13 }}>⚠ Online, but GPS has not reached the map</strong>
              <div style={{ color: '#cbd5e1', fontSize: 12, marginTop: 3 }}>They are marked online, but with no location they are not offered local rides. Call them to open the Anna app and restore location.</div>
            </div>
            <span style={{ color: '#fbbf24', fontWeight: 800, fontSize: 13 }}>{onlineWithoutLocation.length}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {onlineWithoutLocation.map((driver) => (
              <div key={driver.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 9px', borderRadius: 8, background: '#1e293b', border: '1px solid #475569' }}>
                <button
                  type="button"
                  onClick={() => setSelectedDriverId(driver.id)}
                  style={{ border: 'none', background: 'transparent', color: '#f8fafc', cursor: 'pointer', fontWeight: 700, fontSize: 12, padding: 0 }}
                  title="View Anna details"
                >
                  {driver.name || 'Anna'}
                </button>
                {driver.phone && (
                  <a href={phoneHref(driver.phone)} style={{ color: '#7dd3fc', fontSize: 13, textDecoration: 'none' }} title={`Call ${driver.name || 'Anna'}`}>📞</a>
                )}
                {driver.phone && (
                  <a href={waHref(driver.phone, `నమస్కారం ${driver.name || ''} అన్నా! దయచేసి MyTownly Anna యాప్ తెరిచి లొకేషన్ ఆన్‌లో ఉందో చెక్ చేయండి.`)} target="_blank" rel="noreferrer" style={{ color: '#6ee7b7', fontSize: 13, textDecoration: 'none' }} title={`WhatsApp ${driver.name || 'Anna'}`}>💬</a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Main Map with Driver Popover */}
      <div style={{ position: 'relative', flex: 1, minHeight: 420, borderRadius: 14, overflow: 'hidden', border: '1px solid #334155' }}>
        {error ? (
          <div className="empty">{error}</div>
        ) : (
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        )}

        {/* Selected Driver Inspection Popover */}
        {selectedDriver && (
          <div style={{
            position: 'absolute', bottom: 20, left: 20, background: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid #38bdf8', padding: '16px 20px', borderRadius: 14, color: '#fff',
            maxWidth: 340, backdropFilter: 'blur(10px)', zIndex: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>🛺</span>
                <strong style={{ fontSize: 15, color: '#f8fafc' }}>{selectedDriver.name || 'Anna'}</strong>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDriverId(null)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16 }}
              >
                ✕
              </button>
            </div>

            <div style={{ fontSize: 12, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div>Auto Plate: <strong style={{ color: '#fbbf24' }}>{selectedDriver.vehicleNumber || '—'}</strong></div>
              <div>Phone: <strong>{formatPhone(selectedDriver.phone)}</strong></div>
              <div>Status: <span style={{ color: selectedDriver.state === 'online_free' ? '#10b981' : selectedDriver.state === 'trip' ? '#38bdf8' : selectedDriver.state === 'reconnecting' ? '#f59e0b' : '#fbbf24', fontWeight: 700 }}>
                {selectedDriver.stateLabel}
              </span></div>
              <div>Rating: <strong style={{ color: '#fbbf24' }}>{selectedDriver.stats.avgRating ? `★ ${selectedDriver.stats.avgRating}` : '⭐ New (0 reviews)'}</strong></div>
              <div>Today’s Fares: <strong>₹{selectedDriver.stats.todayGross} ({selectedDriver.stats.todayRides} trips)</strong></div>

              {selectedDriver.activeTrip && (
                <div style={{ marginTop: 6, padding: '8px 10px', background: '#0f172a', borderRadius: 8, border: '1px solid #334155' }}>
                  <div style={{ fontSize: 11, color: '#38bdf8', fontWeight: 700 }}>CURRENT TRIP ({selectedDriver.activeTrip.status})</div>
                  <div>Passenger: {selectedDriver.activeTrip.customerName || 'Passenger'}</div>
                  <div>Fare: ₹{selectedDriver.activeTrip.fare}</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                {selectedDriver.phone && (
                  <a
                    href={phoneHref(selectedDriver.phone)}
                    style={{ flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: 8, background: '#0284c7', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 12 }}
                  >
                    📞 Call Anna
                  </a>
                )}
                {selectedDriver.phone && (
                  <a
                    href={waHref(selectedDriver.phone, `నమస్కారం ${selectedDriver.name} అన్నా!`)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: 8, background: '#128C7E', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 12 }}
                  >
                    💬 WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
